import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userCanPrintOrderCover, userIsBuero, userKuerzelFromUser } from "@/lib/auth";
import { resolveMaterialUnit } from "@/lib/materialUnits";
import { listOrders as listAuftraege } from "@/lib/projects";
import {
  composeOrderMail,
  createOrderDraft,
  deleteOrderDraft,
  finalizeOrder,
  getOrderDeliveryState,
  listOrderDrafts,
  patchOrderMailContent,
  searchHeroSuppliers,
  sendOrderMail,
  updateOrder,
  updateOrderDraft,
  cancelOrder,
  restoreOrder,
  markOrderDelivered,
  unmarkOrderDelivered,
  findOrderHistoryByBestellnummer,
  type OrderDraft,
  type OrderDraftIn,
  type OrderHistoryRow,
  type AuftragMini,
} from "@/lib/orders";
import {
  buildMailtoHref,
  clearLocalDraft,
  deliveryFieldsFromOrderState,
  emptyWizardState,
  persistDraftId,
  persistLocalDraft,
  resolveDraftId,
  orderTextWithArticleNumber,
  stateToDraftIn,
  type OrderWizardState,
} from "@/lib/orderDraftUtils";
import { supplierLogoState } from "@/lib/orderSupplierAutocomplete";
import { plainBodyToSignedMailHtml } from "@/lib/mailSignature";
import { OrderWizardDialog } from "@/pages/bestellungen/OrderWizardDialog";
import { CoverPrintDialog } from "@/pages/bestellungen/CoverPrintDialog";
import { MailReviewDialog, type MailReviewResult } from "@/pages/bestellungen/MailReviewDialog";
import { AuftragPickerDialog } from "@/pages/bestellungen/AuftragPickerDialog";
import { OrderDetailDialog } from "@/pages/bestellungen/OrderDetailDialog";
import {
  OrderStornoDialog,
  type OrderStornoDialogMode,
} from "@/pages/bestellungen/OrderStornoDialog";
import {
  OrderWizardContext,
  type OrderWizardChangedHandler,
  type OpenNewOrderOptions,
  type OrderWizardContextValue,
  type OrderWizardNoticeHandler,
} from "@/pages/bestellungen/orderWizardContextValue";

function orderIdsFromFinalize(data: { order_ids?: Record<string, number> }): number[] {
  return Object.values(data.order_ids || {})
    .map((x) => Number(x))
    .filter((x) => Number.isFinite(x) && x > 0);
}

function isMailContentComplete(subject: string, bodyPlain: string): boolean {
  return !!(subject.trim() && bodyPlain.trim());
}

function bestellnummernText(map: Record<string, string> | undefined): string {
  return Object.values(map || {})
    .map((v) => String(v || "").trim())
    .filter(Boolean)
    .join(", ");
}

function auftragIdsFromDraft(draft: OrderDraftIn): number[] {
  return draft.auftraege.map((a) => Number(a.id)).filter((id) => Number.isFinite(id) && id > 0);
}

function mapPositionForReorder(
  p: NonNullable<OrderHistoryRow["positionen"]>[number],
  auftragId: number,
) {
  const hasHero = !!p.hero_nr;
  return {
    typ: hasHero ? ("hero" as const) : ("freitext" as const),
    menge: p.menge ?? 1,
    einheit: resolveMaterialUnit(p.einheit || (hasHero ? p.hero_unit : "")),
    text: orderTextWithArticleNumber(
      p.text || (hasHero ? "Artikel" : ""),
      p.hero_nr,
    ),
    hero_nr: hasHero ? p.hero_nr : null,
    hero_ean: hasHero ? p.hero_ean : null,
    hero_unit: hasHero ? p.hero_unit : null,
    auftrag_id: auftragId,
    localId: `re_${Math.random()}`,
  };
}

export function OrderWizardProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const kuerzel = useMemo(() => userKuerzelFromUser(user), [user]);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardState, setWizardState] = useState<OrderWizardState>(() => emptyWizardState());
  const [wizardTitle, setWizardTitle] = useState("Neue Bestellung");

  const [mailReview, setMailReview] = useState<{
    open: boolean;
    orderIds: number[];
    auftragIds: number[];
    pendingDraft: OrderDraftIn | null;
    bestellnummern: string;
    to: string;
    subject: string;
    bodyHtml: string;
  }>({
    open: false,
    orderIds: [],
    auftragIds: [],
    pendingDraft: null,
    bestellnummern: "",
    to: "",
    subject: "",
    bodyHtml: "",
  });

  const mailReviewRef = useRef(mailReview);
  mailReviewRef.current = mailReview;

  const [auftragPickerOpen, setAuftragPickerOpen] = useState(false);
  const [drafts, setDrafts] = useState<OrderDraft[]>([]);
  const [reorderContext, setReorderContext] = useState<OrderHistoryRow | null>(null);
  const [reorderIndices, setReorderIndices] = useState<number[] | null>(null);
  const [detailOrder, setDetailOrder] = useState<OrderHistoryRow | null>(null);
  const [detailStorno, setDetailStorno] = useState<{
    open: boolean;
    mode: OrderStornoDialogMode;
    row: OrderHistoryRow | null;
    loading: boolean;
  }>({ open: false, mode: "cancel", row: null, loading: false });

  const [coverPrintOpen, setCoverPrintOpen] = useState(false);

  const canMarkDelivered = userIsBuero(user);
  const canPrintOrderCover = userCanPrintOrderCover(user);

  const noticeListenersRef = useRef<Set<OrderWizardNoticeHandler>>(new Set());
  const changedListenersRef = useRef<Set<OrderWizardChangedHandler>>(new Set());

  const isEntra = String(user?.auth_provider || "").toLowerCase() === "entra";

  const emitNotice = useCallback((message: string) => {
    noticeListenersRef.current.forEach((listener) => {
      try {
        listener(message);
      } catch (err) {
        console.warn("[OrderWizardProvider] notice listener failed", err);
      }
    });
  }, []);

  const emitOrdersChanged = useCallback(() => {
    changedListenersRef.current.forEach((listener) => {
      try {
        listener();
      } catch (err) {
        console.warn("[OrderWizardProvider] changed listener failed", err);
      }
    });
  }, []);

  const reloadDraftsList = useCallback(async () => {
    try {
      const list = await listOrderDrafts(true);
      setDrafts(Array.isArray(list) ? list : []);
    } catch {
      setDrafts([]);
    }
  }, []);

  const openNew = useCallback((opts?: OpenNewOrderOptions) => {
    clearLocalDraft();
    setMailReview((m) => ({ ...m, open: false, pendingDraft: null }));
    const next = emptyWizardState(kuerzel);
    if (opts?.auftrag) {
      next.auftraege = [opts.auftrag];
      next.placeholders = 0;
    }
    setWizardState(next);
    setWizardTitle("Neue Bestellung");
    setWizardOpen(true);
    void reloadDraftsList();
  }, [kuerzel, reloadDraftsList]);

  const openDraft = useCallback(
    (d: OrderDraft) => {
      persistDraftId(d.id);
      setWizardState({
        ...emptyWizardState(d.source_ma_id ? kuerzel : d.kuerzel || kuerzel),
        supplier: d.supplier_id
          ? {
              id: d.supplier_id,
              name: d.supplier_name || "",
              contact_first_name: d.supplier_contact_first_name || "",
              contact_last_name: d.supplier_contact_last_name || "",
              anrede: d.supplier_contact_anrede || "",
              email: d.supplier_email || "",
              person: d.supplier_contact_name || "",
            }
          : null,
        templateId: d.template_id || null,
        auftraege: d.auftraege || [],
        positionen: (d.positionen || []).map((p) => ({
          ...p,
          text: orderTextWithArticleNumber(p.text, p.hero_nr),
          einheit: resolveMaterialUnit(p.einheit),
          localId: `pos_${p.auftrag_id}_${Math.random()}`,
        })),
        draftId: d.id,
        sourceMaId: d.source_ma_id ?? null,
        sourceAnfrageId: d.source_anfrage_id ?? null,
        lieferadresseId: d.lieferadresse_id ?? null,
        lieferadresseZhd: d.lieferadresse_zhd || "",
        lieferadresseFirma: d.lieferadresse_firma || "",
        lieferadresseStrasse: d.lieferadresse_strasse || "",
        lieferadresseHausnummer: d.lieferadresse_hausnummer || "",
        lieferadressePostleitzahl: d.lieferadresse_postleitzahl || "",
        lieferadresseStadt: d.lieferadresse_stadt || "",
        lieferart: d.lieferart === "abholung" ? "abholung" : "lieferung",
        placeholders: 0,
      });
      setWizardTitle("Bestellung (Entwurf)");
      setWizardOpen(true);
    },
    [kuerzel],
  );

  const openDraftById = useCallback(
    async (id: number, title?: string) => {
      const list = await listOrderDrafts(true);
      const d = list.find((x) => x.id === id);
      if (d) {
        openDraft(d);
        if (title) setWizardTitle(title);
      } else {
        emitNotice("Entwurf nicht gefunden.");
      }
    },
    [openDraft, emitNotice],
  );

  const autosaveDraft = useCallback(async (): Promise<boolean> => {
    if (Number(wizardState.editingOrderId) > 0) return true;
    const payload = stateToDraftIn(wizardState);
    const existingId = resolveDraftId(wizardState);
    let savedDraftId = existingId;
    try {
      if (existingId) {
        try {
          await updateOrderDraft(existingId, payload);
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) {
            const d = await createOrderDraft(payload);
            savedDraftId = d.id;
          } else {
            throw err;
          }
        }
      } else {
        const d = await createOrderDraft(payload);
        savedDraftId = d.id;
      }
      if (savedDraftId) {
        setWizardState((s) => ({ ...s, draftId: savedDraftId }));
        persistDraftId(savedDraftId);
        persistLocalDraft({ ...wizardState, draftId: savedDraftId });
      }
      await reloadDraftsList();
      emitOrdersChanged();
      return true;
    } catch {
      return false;
    }
  }, [wizardState, reloadDraftsList, emitOrdersChanged]);

  const closeWizard = useCallback(
    async (saveOnClose: boolean) => {
      if (Number(wizardState.editingOrderId) > 0) {
        setWizardOpen(false);
        emitOrdersChanged();
        return;
      }
      if (saveOnClose && wizardState.positionen.length) {
        const ok = await autosaveDraft();
        if (ok) clearLocalDraft();
      } else if (!wizardState.positionen.length) {
        const draftId = resolveDraftId(wizardState);
        if (draftId) {
          try {
            await deleteOrderDraft(draftId);
          } catch {
            /* ignore */
          }
        }
        clearLocalDraft();
      }
      setWizardOpen(false);
      emitOrdersChanged();
    },
    [wizardState, autosaveDraft, emitOrdersChanged],
  );

  const cleanupAfterMail = useCallback(async () => {
    setMailReview((m) => ({ ...m, open: false, pendingDraft: null, orderIds: [] }));
    if (wizardState.draftId) {
      try {
        await deleteOrderDraft(wizardState.draftId);
      } catch {
        /* ignore */
      }
    }
    clearLocalDraft();
    setWizardOpen(false);
    emitOrdersChanged();
    void reloadDraftsList();
  }, [wizardState.draftId, emitOrdersChanged, reloadDraftsList]);

  const openMailPreview = useCallback(
    async (params: {
      to: string;
      draft: OrderDraftIn;
      orderIds?: number[];
      pendingFinalize?: boolean;
      subject?: string;
      body?: string;
      bestellnummern?: Record<string, string>;
    }) => {
      let subject = (params.subject || "").trim();
      let body = (params.body || "").trim();
      let bestellnummernMap = params.bestellnummern;

      if (!subject || !body) {
        const composed = await composeOrderMail(params.draft);
        subject = subject || (composed.subject || "").trim();
        body = body || (composed.body || "").trim();
        bestellnummernMap = bestellnummernMap || composed.bestellnummern;
      }

      if (!isMailContentComplete(subject, body)) {
        emitNotice(
          "E-Mail-Text konnte nicht erzeugt werden. Bitte Lieferant, Auftrag und Positionen prüfen.",
        );
        return;
      }

      const bodyHtml = await plainBodyToSignedMailHtml(body);
      setWizardOpen(false);
      setMailReview({
        open: true,
        orderIds: params.orderIds || [],
        auftragIds: auftragIdsFromDraft(params.draft),
        pendingDraft: params.pendingFinalize ? params.draft : null,
        bestellnummern: bestellnummernText(bestellnummernMap),
        to: params.to,
        subject,
        bodyHtml,
      });
    },
    [emitNotice],
  );

  const startOrderFlow = useCallback(async () => {
    if (!wizardState.positionen.length) {
      emitNotice("Bitte mindestens eine Position hinzufügen.");
      return;
    }
    const recipient = (wizardState.supplier?.email || "").trim();
    try {
      await openMailPreview({
        to: recipient,
        draft: stateToDraftIn(wizardState),
        pendingFinalize: true,
      });
    } catch (err) {
      emitNotice(err instanceof Error ? err.message : "Bestellung fehlgeschlagen.");
    }
  }, [wizardState, openMailPreview, emitNotice]);

  const startPrintFlow = useCallback(() => {
    if (!wizardState.positionen.length) {
      emitNotice("Bitte mindestens eine Position hinzufügen.");
      return;
    }
    setCoverPrintOpen(true);
  }, [wizardState.positionen.length, emitNotice]);

  const finalizeForPrint = useCallback(async (): Promise<number[]> => {
    const finalized = await finalizeOrder(stateToDraftIn(wizardState), resolveDraftId(wizardState));
    const orderIds = orderIdsFromFinalize(finalized);
    if (!orderIds.length) {
      throw new Error("Bestellung wurde angelegt, aber keine ID erhalten.");
    }
    if (wizardState.draftId) {
      try {
        await deleteOrderDraft(wizardState.draftId);
      } catch {
        /* ignore */
      }
    }
    clearLocalDraft();
    setWizardOpen(false);
    emitOrdersChanged();
    void reloadDraftsList();
    return orderIds;
  }, [wizardState, emitOrdersChanged, reloadDraftsList]);

  const handleCoverPrintDone = useCallback(
    (message: string) => {
      emitNotice(message);
    },
    [emitNotice],
  );

  const handleMailReviewResult = useCallback(
    async (result: MailReviewResult) => {
      const review = mailReviewRef.current;
      if (result.action === "back") {
        setMailReview((m) => ({ ...m, open: false }));
        setWizardOpen(true);
        return;
      }

      if (!isMailContentComplete(result.subject, result.bodyPlain)) {
        emitNotice("Betreff und Nachricht dürfen nicht leer sein.");
        return;
      }

      if (result.action === "graph") {
        const recipient = (
          result.to ||
          review.to ||
          review.pendingDraft?.supplier_email ||
          ""
        ).trim();
        if (!recipient) {
          emitNotice(
            "Bitte eine Empfänger-E-Mail-Adresse eintragen. Die Bestellung wird erst nach erfolgreichem Versand gespeichert.",
          );
          return;
        }
      }

      let orderIds = review.orderIds;
      let bestellnummern = review.bestellnummern;

      if (review.pendingDraft) {
        try {
          const finalized = await finalizeOrder(
            review.pendingDraft,
            resolveDraftId(wizardState),
          );
          orderIds = orderIdsFromFinalize(finalized);
          bestellnummern = bestellnummernText(finalized.bestellnummern);
          if (!orderIds.length) {
            emitNotice("Bestellung konnte nicht angelegt werden.");
            return;
          }
        } catch (err) {
          emitNotice(err instanceof Error ? err.message : "Bestellung fehlgeschlagen.");
          return;
        }
      } else if (!orderIds.length) {
        emitNotice("Bestellung konnte nicht angelegt werden.");
        return;
      }

      setMailReview((m) => ({ ...m, open: false }));

      const patchOpts = (channel: "mailto" | "graph" | "copy") => ({
        bodyHtml: result.bodyHtml,
        submissionChannel: channel,
      });

      if (result.action === "copy") {
        await patchOrderMailContent(orderIds, result.subject, result.bodyPlain, patchOpts("copy"));
        if (bestellnummern) {
          await navigator.clipboard.writeText(bestellnummern);
          emitNotice("Bestellnummer(n) kopiert.");
        } else {
          emitNotice("Keine Bestellnummer vorhanden.");
        }
        await cleanupAfterMail();
        return;
      }
      if (result.action === "mailto") {
        if (result.sharepointItemIds.length || result.attachments.length) {
          emitNotice(
            "SharePoint- und lokale Anhänge bitte manuell im Mailprogramm hinzufügen oder Direktversand nutzen.",
          );
        }
        await patchOrderMailContent(orderIds, result.subject, result.bodyPlain, patchOpts("mailto"));
        const href = buildMailtoHref(result.to, result.subject, result.bodyPlain);
        const a = document.createElement("a");
        a.href = href;
        a.rel = "noopener";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (result.action === "graph") {
        const recipient = (
          result.to ||
          review.to ||
          review.pendingDraft?.supplier_email ||
          ""
        ).trim();
        try {
          await sendOrderMail({
            order_ids: orderIds,
            subject: result.subject,
            body: result.bodyPlain,
            html_body: result.bodyHtml,
            to: recipient || null,
            sharepoint_item_ids: result.sharepointItemIds,
            attachments: result.attachments.length ? result.attachments : undefined,
          });
          emitNotice("Mail gesendet.");
        } catch (err) {
          if (err instanceof ApiError && err.status === 412) {
            emitNotice("Microsoft-Anmeldung muss erneuert werden.");
          } else {
            emitNotice(err instanceof Error ? err.message : "Versand fehlgeschlagen.");
          }
          setMailReview((m) => ({ ...m, open: true, orderIds, pendingDraft: null }));
          return;
        }
      }
      await cleanupAfterMail();
    },
    [cleanupAfterMail, emitNotice],
  );

  const handleUpdate = useCallback(
    async (openMail: boolean) => {
      const id = wizardState.editingOrderId;
      if (!id) return;
      try {
        const data = await updateOrder(id, stateToDraftIn(wizardState));
        if (openMail) {
          const to = (wizardState.supplier?.email || "").trim();
          const orderIdMap =
            data.order_ids && Object.keys(data.order_ids).length
              ? data.order_ids
              : Object.fromEntries(wizardState.auftraege.map((a) => [String(a.id), id]));
          const orderIds = orderIdsFromFinalize({ order_ids: orderIdMap });
          emitOrdersChanged();
          await openMailPreview({
            to,
            draft: stateToDraftIn(wizardState),
            orderIds,
            subject: data.subject,
            body: data.body,
            bestellnummern: data.bestellnummern,
          });
          return;
        }
        const text = Object.values(data.bestellnummern || {}).join(", ");
        if (text) await navigator.clipboard.writeText(text);
        emitNotice("Bestellung aktualisiert.");
        setWizardOpen(false);
        emitOrdersChanged();
      } catch (err) {
        emitNotice(err instanceof Error ? err.message : "Aktualisieren fehlgeschlagen.");
      }
    },
    [wizardState, openMailPreview, emitNotice, emitOrdersChanged],
  );

  const openEdit = useCallback(
    async (row: OrderHistoryRow) => {
      if (row.cancelled_at) {
        emitNotice("Stornierte Bestellungen können nicht bearbeitet werden.");
        return;
      }
      const items = row.positionen || [];
      if (!items.length) {
        emitNotice("Bestellung kann nicht bearbeitet werden.");
        return;
      }
      const auftragIds = [...new Set(items.map((p) => Number(p.auftrag_id)).filter((x) => x > 0))];
      const allProjects = await listAuftraege({ limit: 500 }).catch(() => []);
      const metaMap = new Map(allProjects.map((a) => [Number(a.id), a]));

      clearLocalDraft();
      const auftraege: AuftragMini[] = auftragIds.map((aid) => {
        const firstPos = items.find((p) => Number(p.auftrag_id) === aid);
        const meta = metaMap.get(aid);
        const raw = String(firstPos?.auftragsnummer || meta?.auftragsnummer || "");
        const parts = raw.split("#");
        return {
          id: aid,
          jahr: Number(meta?.jahr) || new Date().getFullYear(),
          ordnernummer: meta?.ordnernummer || parts[0] || "",
          nummer: parts.length > 1 ? Number(parts[1]) : undefined,
          auftragsnummer: raw,
          beschreibung: meta?.beschreibung,
          ort: meta?.ort,
        };
      });

      const editingBn: Record<number, string> = {};
      items.forEach((p) => {
        const aid = Number(p.auftrag_id);
        const bn = String(p.bestellnummer || "").trim();
        if (aid > 0 && bn && !editingBn[aid]) editingBn[aid] = bn;
      });

      let supplier = {
        id: row.supplier_id as string | number | null,
        name: row.supplier_name || "",
        email: row.supplier_email || "",
        anrede: row.supplier_contact_salutation || "",
        contact_first_name: row.supplier_contact_first_name || "",
        contact_last_name: row.supplier_contact_last_name || "",
        person: row.supplier_contact_name || "",
        ...supplierLogoState({
          geschaeftspartner_id: row.supplier_gp_id,
          has_logo: row.supplier_has_logo,
          logo_updated_at: row.supplier_logo_updated_at,
        }),
      };
      if (!supplier.id && supplier.name) {
        const arr = await searchHeroSuppliers(supplier.name);
        const exact = arr.find(
          (x) => (x.name || "").trim().toLowerCase() === supplier.name.trim().toLowerCase(),
        );
        const picked = exact || arr[0];
        if (picked?.id != null) supplier = { ...supplier, id: picked.id, ...supplierLogoState(picked) };
        else supplier = { ...supplier, id: `name:${supplier.name.trim().toLowerCase()}` };
      }

      const delivery = await getOrderDeliveryState(row.id).catch(() => null);
      const deliveryFields = delivery
        ? deliveryFieldsFromOrderState(delivery)
        : deliveryFieldsFromOrderState({
            lieferart: row.lieferart || "lieferung",
          });

      setWizardState({
        ...emptyWizardState(row.kuerzel || kuerzel),
        editingOrderId: row.id,
        editingBestellnummerText: row.bestellnummern || "",
        editingBestellnummerByAuftrag: editingBn,
        templateId: row.template_id || null,
        supplier,
        auftraege,
        positionen: items.map((p) => ({
          typ: p.hero_nr ? "hero" : "freitext",
          menge: Number(p.menge) || 0,
          einheit: resolveMaterialUnit(p.einheit),
          text: orderTextWithArticleNumber(p.text || "", p.hero_nr),
          hero_nr: p.hero_nr || null,
          hero_ean: p.hero_ean || null,
          hero_unit: p.hero_unit || null,
          auftrag_id: Number(p.auftrag_id),
          lot_nummer: p.lot_nummer || null,
          localId: `edit_${Math.random()}`,
        })),
        ...deliveryFields,
        placeholders: 0,
      });
      setWizardTitle(`Bestellung bearbeiten · ${row.bestellnummern || ""}`);
      setWizardOpen(true);
    },
    [kuerzel, emitNotice],
  );

  const beginReorder = useCallback((row: OrderHistoryRow, indices: number[] | null) => {
    setReorderContext(row);
    setReorderIndices(indices);
    setAuftragPickerOpen(true);
  }, []);

  const openDetail = useCallback((row: OrderHistoryRow) => {
    setDetailOrder(row);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailOrder(null);
  }, []);

  const openDetailByBestellnummer = useCallback(
    async (bestellnummer: string) => {
      try {
        const row = await findOrderHistoryByBestellnummer(bestellnummer);
        if (!row) {
          emitNotice("Bestellung nicht gefunden.");
          return;
        }
        setDetailOrder(row);
      } catch {
        emitNotice("Bestellung konnte nicht geladen werden.");
      }
    },
    [emitNotice],
  );

  const openDetailStorno = useCallback((mode: OrderStornoDialogMode, row: OrderHistoryRow) => {
    setDetailStorno({ open: true, mode, row, loading: false });
  }, []);

  const closeDetailStorno = useCallback(() => {
    setDetailStorno((prev) => ({ ...prev, open: false, loading: false }));
  }, []);

  const handleDetailMarkDelivered = useCallback(
    async (row: OrderHistoryRow) => {
      const result = await markOrderDelivered(row.id);
      setDetailOrder((prev) =>
        prev?.id === row.id ? { ...prev, geliefert_at: result.geliefert_at ?? prev.geliefert_at } : prev,
      );
      emitOrdersChanged();
    },
    [emitOrdersChanged],
  );

  const handleDetailUnmarkDelivered = useCallback(
    async (row: OrderHistoryRow) => {
      await unmarkOrderDelivered(row.id);
      setDetailOrder((prev) => (prev?.id === row.id ? { ...prev, geliefert_at: null } : prev));
      emitOrdersChanged();
    },
    [emitOrdersChanged],
  );

  const handleDetailStornoConfirm = useCallback(async () => {
    const { mode, row } = detailStorno;
    if (!row) return;
    setDetailStorno((prev) => ({ ...prev, loading: true }));
    try {
      if (mode === "cancel") {
        await cancelOrder(row.id);
        emitNotice("Bestellung wurde storniert.");
      } else {
        await restoreOrder(row.id);
        emitNotice("Stornierung wurde aufgehoben.");
      }
      closeDetailStorno();
      setDetailOrder(null);
      emitOrdersChanged();
    } catch (err) {
      setDetailStorno((prev) => ({ ...prev, loading: false }));
      emitNotice(
        err instanceof ApiError
          ? err.message
          : mode === "cancel"
            ? "Stornierung fehlgeschlagen."
            : "Aufheben der Stornierung fehlgeschlagen.",
      );
    }
  }, [detailStorno, closeDetailStorno, emitNotice, emitOrdersChanged]);

  const applyReorder = useCallback(
    async (auftrag: AuftragMini) => {
      const row = reorderContext;
      if (!row) return;
      const items = row.positionen || [];
      const picked =
        reorderIndices === null ? items : reorderIndices.map((i) => items[i]).filter(Boolean);

      let supplier = {
        id: row.supplier_id as string | number | null,
        name: row.supplier_name || "",
        email: row.supplier_email || "",
        anrede: row.supplier_contact_salutation || "",
        contact_first_name: row.supplier_contact_first_name || "",
        contact_last_name: row.supplier_contact_last_name || "",
        person: row.supplier_contact_name || "",
        ...supplierLogoState({
          geschaeftspartner_id: row.supplier_gp_id,
          has_logo: row.supplier_has_logo,
          logo_updated_at: row.supplier_logo_updated_at,
        }),
      };
      if (!supplier.id && supplier.name) {
        const arr = await searchHeroSuppliers(supplier.name);
        const pickedS =
          arr.find(
            (x) => (x.name || "").trim().toLowerCase() === supplier.name.trim().toLowerCase(),
          ) || arr[0];
        if (pickedS?.id != null) supplier = { ...supplier, id: pickedS.id, ...supplierLogoState(pickedS) };
      }

      setWizardState({
        ...emptyWizardState(kuerzel),
        supplier,
        auftraege: [auftrag],
        positionen: picked.filter(Boolean).map((p) => mapPositionForReorder(p!, auftrag.id)),
        placeholders: 0,
      });
      setWizardTitle("Neue Bestellung (Erneut)");
      setWizardOpen(true);
      setReorderContext(null);
      setReorderIndices(null);
    },
    [reorderContext, reorderIndices, kuerzel],
  );

  const subscribeNotice = useCallback((listener: OrderWizardNoticeHandler) => {
    noticeListenersRef.current.add(listener);
    return () => {
      noticeListenersRef.current.delete(listener);
    };
  }, []);

  const subscribeOrdersChanged = useCallback((listener: OrderWizardChangedHandler) => {
    changedListenersRef.current.add(listener);
    return () => {
      changedListenersRef.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    void reloadDraftsList();
  }, [reloadDraftsList]);

  const handleDeleteDraft = useCallback(
    async (id: number) => {
      try {
        await deleteOrderDraft(id);
        emitNotice("Entwurf gelöscht.");
        await reloadDraftsList();
        emitOrdersChanged();
      } catch {
        emitNotice("Entwurf konnte nicht gelöscht werden.");
      }
    },
    [reloadDraftsList, emitNotice, emitOrdersChanged],
  );

  const value = useMemo<OrderWizardContextValue>(
    () => ({
      openNew,
      openDraft,
      openDraftById,
      openEdit,
      openDetail,
      openDetailByBestellnummer,
      closeDetail,
      beginReorder,
      isOpen: wizardOpen,
      subscribeNotice,
      subscribeOrdersChanged,
    }),
    [
      openNew,
      openDraft,
      openDraftById,
      openEdit,
      openDetail,
      openDetailByBestellnummer,
      closeDetail,
      beginReorder,
      wizardOpen,
      subscribeNotice,
      subscribeOrdersChanged,
    ],
  );

  return (
    <OrderWizardContext.Provider value={value}>
      {children}

      <OrderWizardDialog
        open={wizardOpen}
        state={wizardState}
        setState={setWizardState}
        title={wizardTitle}
        drafts={drafts}
        showDraftPicker={wizardTitle === "Neue Bestellung"}
        onLoadDraft={openDraft}
        onDeleteDraft={(id) => void handleDeleteDraft(id)}
        onClose={() => void closeWizard(true)}
        onSaveDraft={async () => {
          if (!wizardState.positionen.length) {
            await closeWizard(false);
            return;
          }
          const ok = await autosaveDraft();
          if (ok) {
            emitNotice("Entwurf gespeichert.");
            clearLocalDraft();
            setWizardOpen(false);
            void reloadDraftsList();
          } else {
            emitNotice("Entwurf konnte nicht gespeichert werden.");
          }
        }}
        onOrder={() => void startOrderFlow()}
        onPrint={startPrintFlow}
        showPrintButton={canPrintOrderCover && Number(wizardState.editingOrderId) <= 0}
        onUpdate={() => void handleUpdate(false)}
        onUpdateAndMail={() => void handleUpdate(true)}
      />

      <CoverPrintDialog
        open={coverPrintOpen}
        orderIds={[]}
        finalizeBeforePrint={finalizeForPrint}
        onClose={() => setCoverPrintOpen(false)}
        onDone={handleCoverPrintDone}
      />

      <MailReviewDialog
        open={mailReview.open}
        orderIds={mailReview.orderIds}
        auftragIds={mailReview.auftragIds}
        initialTo={mailReview.to}
        initialSubject={mailReview.subject}
        initialBodyHtml={mailReview.bodyHtml}
        isEntra={isEntra}
        onResolve={(r) => void handleMailReviewResult(r)}
      />

      <AuftragPickerDialog
        open={auftragPickerOpen}
        onClose={() => {
          setAuftragPickerOpen(false);
          setReorderContext(null);
        }}
        onPick={(a) => void applyReorder(a)}
      />

      <OrderDetailDialog
        open={detailOrder != null}
        order={detailOrder}
        canMarkDelivered={canMarkDelivered}
        zIndexClass="z-[210]"
        onClose={closeDetail}
        onOrderChange={(next) => {
          setDetailOrder(next);
          emitOrdersChanged();
        }}
        onReorderAll={(row) => {
          closeDetail();
          beginReorder(row, null);
        }}
        onReorderOne={(row, idx) => {
          closeDetail();
          beginReorder(row, [idx]);
        }}
        onEdit={(row) => {
          closeDetail();
          void openEdit(row);
        }}
        onRequestCancel={(row) => openDetailStorno("cancel", row)}
        onRequestRestore={(row) => openDetailStorno("restore", row)}
        onMarkDelivered={handleDetailMarkDelivered}
        onUnmarkDelivered={handleDetailUnmarkDelivered}
      />

      <OrderStornoDialog
        open={detailStorno.open}
        mode={detailStorno.mode}
        row={detailStorno.row}
        loading={detailStorno.loading}
        onClose={closeDetailStorno}
        onConfirm={() => void handleDetailStornoConfirm()}
      />
    </OrderWizardContext.Provider>
  );
}
